import PageHeader from '@Components/PageHeader';
import { Card, CardHeader, CardTitle, CardContent } from '@Components/Card';
import { Combobox } from '@Components/Combobox';
import { useToastGuidSettings } from '@Hooks/useToastGuidSettings';
import { useToastGuidGroups } from '@Hooks/useToastGuidGroups';
import { useUpdateToastGuidSetting } from '@Hooks/useUpdateToastGuidSetting';
import {
  toastSuccess,
  toastError,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/toast';
import { Loader2 } from 'lucide-react';
import { useMemo } from 'react';
import { type ToastGuidSetting } from '@Types/toastGuidSetting';

const NONE_VALUE = '__none__';

/** Convert a SCREAMING_SNAKE type string to a readable label (e.g. "MANUAL_CHARGES" → "Manual Charges"). */
const formatTypeLabel = (type: string): string =>
  type
    .split('_')
    .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
    .join(' ');

export default function ToastGuidSettings() {
  const { data: settingsData, isLoading: settingsLoading } =
    useToastGuidSettings({
      searchParams: { 'include': 'group', 'page[size]': '-1' },
    });

  const { data: groupsData, isLoading: groupsLoading } = useToastGuidGroups({
    searchParams: { 'page[size]': '-1' },
  });

  const updateMutation = useUpdateToastGuidSetting();

  const groupItems = useMemo(() => {
    const groups = groupsData?.data ?? [];
    return [
      { value: NONE_VALUE, label: 'None' },
      ...groups.map((group) => ({
        value: group.id,
        label: group.name,
      })),
    ];
  }, [groupsData]);

  const settings = useMemo(() => {
    return settingsData?.data ?? [];
  }, [settingsData]);

  const categorizedSettings = useMemo(() => {
    const sorted = [...settings].sort((a, b) =>
      a.name.localeCompare(b.name),
    );

    const grouped = new Map<
      string,
      { label: string; settings: ToastGuidSetting[] }
    >();

    for (const setting of sorted) {
      const typeKey = setting.type ?? 'OTHER';
      const label = formatTypeLabel(typeKey);

      if (!grouped.has(typeKey)) {
        grouped.set(typeKey, { label, settings: [] });
      }
      grouped.get(typeKey)!.settings.push(setting);
    }

    return Array.from(grouped.values());
  }, [settings]);

  const handleGroupChange = (settingId: string, selectedValue: string) => {
    const groupId = selectedValue === NONE_VALUE ? null : selectedValue;
    updateMutation.mutate(
      { id: settingId, payload: { groupId } },
      {
        onSuccess: () => {
          toastSuccess('Setting updated successfully');
        },
        onError: (error) => {
          toastError(
            error instanceof Error ? error.message : 'Failed to update setting',
          );
        },
      },
    );
  };

  if (settingsLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-zinc-400" />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        navList={[
          { name: 'Settings', href: '/settings' },
          {
            name: 'Toast GUID Settings',
            href: '/settings/toast-guid-settings',
          },
        ]}
        title="Toast GUID Settings"
      />
      <div className="flex flex-col gap-6">
        {categorizedSettings.map((category) => (
          <Card key={category.label}>
            <CardHeader>
              <CardTitle>{category.label}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="divide-y divide-zinc-200">
                {category.settings.map((setting) => (
                  <div
                    key={setting.id}
                    className="flex items-center justify-between py-3 first:pt-0 last:pb-0"
                  >
                    <div className="flex flex-col gap-0.5">
                      <span className="font-medium">{setting.name}</span>
                      {setting.description && (
                        <span className="text-sm text-zinc-500">
                          {setting.description}
                        </span>
                      )}
                    </div>
                    <div className="w-64">
                      <Combobox
                        items={groupItems}
                        value={setting.groupId ?? NONE_VALUE}
                        onChange={(value) =>
                          handleGroupChange(setting.id, value)
                        }
                        placeholder="Select a group"
                        loading={groupsLoading}
                        disabled={updateMutation.isPending}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
        {settings.length === 0 && (
          <p className="py-8 text-center text-zinc-500">No settings found.</p>
        )}
      </div>
    </div>
  );
}
